import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CAMPAIGN_LOGO = 'https://customer-assets.emergentagent.com/job_bba65e17-d01f-4e0d-ac4c-a997107c6926/artifacts/tvydclge_1D4B9B2B-E0EB-46B4-B318-D85B7D6F1D85.png';
const CONTACT_EMAIL = 'cliandaflorence@friendsofcliandaflorence.com';

const H2 = ({ children }) => (
  <h2 className="text-2xl font-bold text-purple-950 mt-10 mb-4 leading-snug">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-base text-slate-700 leading-relaxed mb-4">{children}</p>
);

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="terms-conditions-page">
      <header className="bg-purple-900 py-4 sticky top-0 z-50 shadow-md">
        <div className="container mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0 transition-transform duration-300 hover:scale-105" data-testid="legal-logo-home-link">
            <img src={CAMPAIGN_LOGO} alt="Clianda Florence Campaign Logo" className="h-12 md:h-14 w-auto" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-white hover:text-purple-200 font-semibold text-sm uppercase tracking-wide transition-colors" data-testid="legal-back-home-link">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="container mx-auto px-6 max-w-3xl py-12 lg:py-20">
          <p className="text-purple-700 font-semibold uppercase tracking-wide text-sm mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-purple-950 leading-tight tracking-tight mb-4">
            Terms &amp; Conditions
          </h1>
          <p className="text-sm text-slate-500 mb-10 pb-8 border-b border-slate-200">
            Last Updated: October 21, 2026
          </p>

          <P>
            These Terms &amp; Conditions apply to SMS/MMS text messaging and other campaign communications from
            Friends of Clianda Florence.
          </P>
          <P>
            For information about how Friends of Clianda Florence collects, uses, and protects information, please
            review our{' '}
            <Link to="/privacy-policy/" className="text-purple-700 font-semibold underline hover:text-purple-900" data-testid="terms-privacy-link">
              Privacy Policy
            </Link>
            .
          </P>

          <H2>SMS/MMS Text Messaging Terms</H2>
          <P>
            By entering your phone number and selecting to opt in, you agree to receive recurring SMS/MMS text
            messages from Friends of Clianda Florence, including campaign alerts, donation requests, updates, voter
            outreach, event invitations, polling questions, get-out-the-vote reminders, election information, and
            other important campaign information.
          </P>
          <P>Message frequency varies. Msg &amp; data rates may apply.</P>
          <P>
            Friends of Clianda Florence does not charge for text messaging services, but your mobile
            carrier&rsquo;s standard messaging, data, and other rates and fees may apply. Please contact your
            carrier for details about your mobile plan.
          </P>
          <P>You may opt out at any time by replying STOP to any text message. You may also reply HELP for help.</P>
          <P>
            For assistance, reply HELP to any message or contact Friends of Clianda Florence at:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-700 font-semibold underline hover:text-purple-900">
              {CONTACT_EMAIL}
            </a>
          </P>
          <P>
            Consent to receive SMS/MMS text messages is not a condition of making a donation, volunteering,
            supporting the campaign, or using this website.
          </P>

          <H2>Mobile Opt-In Data</H2>
          <P>
            Friends of Clianda Florence does not sell, rent, or share mobile opt-in data or SMS consent with third
            parties for their own marketing purposes. Mobile phone numbers and SMS consent information may be
            shared with service providers only as necessary to operate campaign communications, comply with law,
            or support campaign operations.
          </P>
          <P>Carriers are not liable for delayed or undelivered messages.</P>
          <P>
            By participating in the Friends of Clianda Florence SMS/MMS messaging program, you agree to these Terms
            &amp; Conditions and the{' '}
            <Link to="/privacy-policy/" className="text-purple-700 font-semibold underline hover:text-purple-900">
              Privacy Policy
            </Link>
            .
          </P>

          <p className="text-base font-bold text-purple-950 mt-14 pt-8 border-t border-slate-200">
            Paid for by Friends of Clianda Florence.
          </p>
        </div>
      </main>

      <footer className="bg-slate-900 py-8 text-center">
        <Link to="/" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="legal-footer-home-link">
          Return to friendsofcliandaflorence.com
        </Link>
        <p className="text-xs text-slate-500 mt-3">
          © {new Date().getFullYear()} Friends of Clianda Florence. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default TermsConditions;
